import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { SwitchCase } from "@/frameworks/libraries/components";

import { Avatar, Grid, Row } from "../../../components";

export interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	avatarList: ReturnType<typeof Avatar>[];
	size?: UnitString;
}

export default function AvatarGroup({ avatarList, size = "40px", ...rest }: AvatarGroupProps) {
	const presentAvatarList = avatarList.slice(0, 4);

	return (
		<Row css={[defaultCss(size)]} {...rest}>
			<SwitchCase
				condition={presentAvatarList.length}
				cases={{
					1: presentAvatarList[0],
					2: (
						<GroupAvatarBackground>
							{presentAvatarList.map((avatar, index) => (
								<AvatarItem key={avatar.key} totalCount={2} count={index + 1} sizes={size}>
									{avatar}
								</AvatarItem>
							))}
						</GroupAvatarBackground>
					),
					3: (
						<GroupAvatarBackground>
							{presentAvatarList.map((avatar, index) => (
								<AvatarItem key={avatar.key} totalCount={3} count={index + 1} sizes={size}>
									{avatar}
								</AvatarItem>
							))}
						</GroupAvatarBackground>
					),
					4: (
						<GroupAvatarBackground>
							<Grid
								column={2}
								row={2}
								css={{ div: { width: `calc(${size} * 5 / 10)`, height: `calc(${size} * 5 / 10)` } }}
							>
								{presentAvatarList.map((avatar) => avatar)}
							</Grid>
						</GroupAvatarBackground>
					)
				}}
				defaultCase={presentAvatarList[0]}
			/>
		</Row>
	);
}

const defaultCss = (size: AvatarGroupProps["size"]) => css`
	position: relative;
	display: inline-flex;
	width: ${size};
	height: ${size};
	min-width: ${size};
	min-height: ${size};
`;

const AvatarItem = styled(Row)<{ totalCount: number; count: number; sizes: AvatarGroupProps["size"] }>`
	position: absolute;
	width: ${({ totalCount, sizes }) => {
		if (totalCount === 2) return `calc(${sizes} * 65 / 100)`;
		return `calc(${sizes} * 56 / 100)`;
	}};
	height: ${({ totalCount, sizes }) => {
		if (totalCount === 2) return `calc(${sizes} * 65 / 100)`;
		return `calc(${sizes} * 56 / 100)`;
	}};
	${({ totalCount, count }) => {
		if (totalCount === 2) {
			if (count === 1) return "top: 0; left: 0";
			else return "bottom: 0; right: 0;";
		} else if (totalCount === 3) {
			if (count === 2) return "top: 0; left: 50%;";
			else if (count === 1) return "bottom: 0; left: 0;";
			else return "bottom: 0; right: 0;";
		}
	}};
	transform: ${({ totalCount, count }) => {
		if (totalCount === 3) {
			if (count === 2) return "translateX(-50%);";
		}
	}};
	div {
		width: 100%;
		height: 100%;
	}
`;

const GroupAvatarBackground = styled.div`
	width: 100%;
	height: 100%;
	border-radius: 12px;
	background: ${({ theme }) => theme.colors.neutral_00_68};
`;
